import {
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarTodayIcon,
  Email as EmailIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationOnIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';

import HauserLogo from '@/assets/images/hauser-logo.svg?react';
import { RequestForQuotation } from '@/models/RequestForQuotation';
import { formatPhone } from '@/utils/formatPhone';

export type RenderMode = 'dark' | 'light';

// Shared minHeight for the "header zone" of left + middle columns so that
// Project: and Next Steps begin at the same Y regardless of how tall the
// address block or the issuer name + contact row turn out to be.
// Tweak this single constant to nudge both columns' bodies up or down together.
export const HEADER_ZONE_MIN_HEIGHT = '120px';

export interface ThemeTokens {
  bg: string;
  card: string;
  text: string;
  textMuted: string;
  textFaint: string;
  divider: string;
  accent: string;
  accentSoft: string;
  cta: string;
}

export const THEMES: Record<RenderMode, ThemeTokens> = {
  // on screen: the document sits on whatever surface holds it (the dialog, the share page's sheet)
  dark: {
    bg: 'transparent',
    card: 'transparent',
    text: 'var(--tx-text)',
    textMuted: 'var(--tx-muted)',
    textFaint: 'var(--tx-faint)',
    divider: 'var(--tx-line)',
    accent: '#C0FF02',
    accentSoft: 'rgba(187,183,175,0.45)',
    cta: '#f3f3f3',
  },
  // the printed paper (the downloaded image): fixed ink on white, never theme tokens, which resolve to the app's dark
  // surfaces and would print a grey page
  light: {
    bg: '#FFFFFF',
    card: '#FFFFFF',
    text: '#000000',
    textMuted: '#333333',
    textFaint: '#666666',
    divider: 'rgba(0,0,0,0.25)',
    accent: '#000000',
    accentSoft: 'rgba(0,0,0,0.1)',
    cta: '#000000',
  },
};

export interface RfqPropertyContext {
  address: string;
  city?: string;
  state?: string;
  zipCode?: string | number;
}

export interface RfqIssuerContext {
  name?: string;
  email?: string;
  phoneNumber?: string;
}

/**
 * Inline-edit controller for the Timeline & Submission fields. When provided
 * to RfqPage / ConsolidatedRfqDocument, the corresponding values render as
 * editable inputs in place of static text so a reviewer can fine-tune the
 * packet before minting a share link.
 */
export interface RfqDocumentEdits {
  desiredStartDate: Date | null;
  deadlineDate: Date | null;
  onDesiredStartChange: (value: Date | null) => void;
  onDeadlineChange: (value: Date | null) => void;
}

interface SectionHeadingProps {
  label: string;
  theme: ThemeTokens;
}

const SectionHeading = ({ label, theme }: SectionHeadingProps) => (
  <Box sx={{ mb: '20px' }}>
    <Typography
      sx={{
        fontFamily: "'Manrope', sans-serif",
        fontSize: { xs: 15, md: 17 },
        fontWeight: 400,
        letterSpacing: '-0.015em',
        color: theme.text,
        mb: '12px',
      }}>
      {label}
    </Typography>
    <Box sx={{ height: '0.5px', backgroundColor: theme.divider }} />
  </Box>
);

const renderInline = (text: string, theme: ThemeTokens) => {
  // Parses **bold** segments inline and renders them with weight 700.
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Box key={i} component="span" sx={{ fontWeight: 600, color: theme.text }}>
          {part.slice(2, -2)}
        </Box>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const inlineEditInputSx = (theme: ThemeTokens) => ({
  '& .MuiInputBase-root': {
    fontFamily: "'Manrope', sans-serif",
    fontSize: { xs: 12, sm: 13, md: 14 },
    color: theme.text,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.divider,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.textMuted,
  },
  '& .MuiInputBase-input': {
    py: '6px',
    px: '8px',
  },
});

interface EditableDateFieldProps {
  label: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  theme: ThemeTokens;
  required?: boolean;
  leadingIcon?: React.ReactNode;
  isMobile?: boolean;
}

const EditableDateField = ({
  label,
  value,
  onChange,
  theme,
  required,
  leadingIcon,
  isMobile,
}: EditableDateFieldProps) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      ref={anchorRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: '4px',
        mb: '8px',
        width: '100%',
      }}>
      {leadingIcon && (
        <Box sx={{ display: 'flex', alignItems: 'center', mr: '4px' }}>{leadingIcon}</Box>
      )}
      <Typography
        sx={{
          fontSize: isMobile ? 12 : { xs: 12, sm: 13, md: 14 },
          fontWeight: 600,
          color: theme.text,
          whiteSpace: 'nowrap',
          minWidth: isMobile ? 'auto' : leadingIcon ? '150px' : '170px',
          flex: isMobile ? '0 0 auto' : '1 1 auto',
        }}>
        {label}:
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: isMobile ? '4px' : '10px',
          ml: isMobile ? 'auto' : '16px',
          flex: isMobile ? '0 0 auto' : '0 0 auto',
        }}>
      <DatePicker
        value={value}
        onChange={onChange}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        desktopModeMediaQuery="@media (min-width: 0px)"
        slotProps={{
          textField: {
            size: 'small',
            InputProps: { endAdornment: null },
            sx: {
              ...inlineEditInputSx(theme),
              width: isMobile ? '110px' : '125px',
              '& .MuiInputBase-root': {
                ...inlineEditInputSx(theme)['& .MuiInputBase-root'],
                fontSize: isMobile ? 12 : 13,
                paddingRight: 0,
                ...(!isMobile && {
                  height: 40,
                  alignItems: 'center',
                }),
              },
              '& .MuiInputBase-input': {
                py: isMobile ? '6px' : 0,
                px: isMobile ? '6px' : '10px',
                textAlign: 'center',
                ...(!isMobile && {
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                }),
              },
            },
          },
          popper: {
            anchorEl: () => anchorRef.current as HTMLElement,
            sx: {
              '& .MuiPaper-root': {
                backgroundColor: 'var(--tx-glass-panel)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow)',
                backdropFilter: 'blur(16px)',
                borderRadius: '24px',
                color: theme.text,
                fontFamily: "'Manrope', sans-serif",
                mt: '4px',
                padding: '20px',
              },
              '& .MuiPickersDay-root': {
                color: theme.text,
                fontFamily: "'Manrope', sans-serif",
                '&.Mui-selected': { backgroundColor: theme.textMuted, color: '#262626' },
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
              },
              '& .MuiPickersCalendarHeader-label': {
                color: theme.text,
                fontFamily: "'Manrope', sans-serif",
              },
              '& .MuiDayCalendar-weekDayLabel': {
                color: theme.textMuted,
                fontFamily: "'Manrope', sans-serif",
              },
              '& .MuiIconButton-root': { color: theme.textMuted },
              '& .MuiPickersYear-yearButton': {
                color: theme.text,
                fontFamily: "'Manrope', sans-serif",
                '&.Mui-selected': { backgroundColor: theme.textMuted, color: '#262626' },
              },
            },
          },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '4px' : '3px',
        }}>
        <IconButton
          onClick={() => setOpen(true)}
          size="small"
          sx={{
            color: theme.textMuted,
            ...(!isMobile && {
              width: 36,
              height: 36,
              p: 0,
            }),
            '&:hover': { color: theme.text },
          }}>
          <CalendarTodayIcon sx={{ fontSize: 18 }} />
        </IconButton>
        {required && (
          <Box
            component="span"
            sx={{
              color: '#FF3EA5',
              fontSize: 17,
              fontWeight: 600,
              lineHeight: 1,
              minWidth: isMobile ? 'auto' : 8,
              textAlign: 'center',
            }}>
            *
          </Box>
        )}
      </Box>
      </Box>
    </Box>
  );
};

interface RfqPageProps {
  rfq: RequestForQuotation;
  property: RfqPropertyContext;
  issuer?: RfqIssuerContext | null;
  theme: ThemeTokens;
  mode: RenderMode;
  edits?: RfqDocumentEdits;
  /** When true, render edits values as static text (used during PDF capture). */
  readOnly?: boolean;
  onIssuerClick?: (event: React.MouseEvent<HTMLElement>) => void;
  issuerSelectable?: boolean;
}

// Matches the local useWindowWidth() convention used elsewhere in this
// codebase (InspectionPunchList.tsx / HomeownerHub.tsx) — window.innerWidth
// over useMediaQuery, and real JS conditional rendering over CSS display
// breakpoint objects, which have proven unreliable in this component.
function useWindowWidth() {
  const [w, setW] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
}

export const RfqPage = ({
  rfq,
  property,
  issuer,
  theme,
  mode,
  edits,
  readOnly,
  onIssuerClick,
  issuerSelectable,
}: RfqPageProps) => {
  const isMobile = useWindowWidth() <= 768;
  const documentCreated = format(new Date(rfq.createdAt), 'MMMM d, yyyy');
  const dueDate = new Date(rfq.createdAt + rfq.submissionRequirements.dueInDays * 86400 * 1000);
  const dueDateFormatted = format(dueDate, 'MMMM d, yyyy');
  // Static-display values that prefer the live edits (so download captures
  // whatever the user just picked, not the persisted backend values).
  const displayStartDate =
    edits?.desiredStartDate != null
      ? format(edits.desiredStartDate, 'MMMM d, yyyy')
      : rfq.timeline.desiredStartDate;
  const displayDeadline =
    edits?.deadlineDate != null ? format(edits.deadlineDate, 'MMMM d, yyyy') : dueDateFormatted;
  const showEditable = Boolean(edits) && !readOnly;

  const cityStateZip = [property.city, [property.state, property.zipCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const mapQuery = [property.address, cityStateZip].filter(Boolean).join(', ');
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          backgroundColor: theme.bg,
          color: theme.text,
          fontFamily: "'Manrope', sans-serif",
          px: { xs: '16px', sm: '20px', md: '48px', lg: '60px' },
          pt: { xs: '16px', sm: '20px', md: '48px', lg: '60px' },
          pb: { xs: '16px', sm: '20px', md: '24px', lg: '30px' },
          maxWidth: '1540px',
          mx: 'auto',
          width: '100%',
          boxSizing: 'border-box',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}>
        {/* Top bar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', mb: '24px' }}>
          <Typography sx={{ fontSize: 24, fontWeight: 300, letterSpacing: '-0.025em', color: theme.text, lineHeight: 1.2 }}>
            Request for Quote
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.textFaint, lineHeight: 1.3, mb: isMobile ? '10px' : 0 }}>
            Document Created:{' '}
            <Box component="span" sx={{ color: theme.text, fontWeight: 600 }}>
              {documentCreated}
            </Box>
          </Typography>
        </Box>

        {/* 3-column by default; collapses to 2-col on tablet portrait, 1-col on phone */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { sm: '1fr', md: '1fr 1fr 1fr' },
            gap: { sm: 0, md: '56px' },
          }}>
          {/* LEFT COLUMN */}
          <Box sx={{ minWidth: 0 }}>
            {/* Header zone wrapper — minHeight reserves space so Project: aligns
                with Next Steps in the middle column. Address row anchors to top. */}
            <Box
              sx={{
                minHeight: { sm: 'auto', md: HEADER_ZONE_MIN_HEIGHT },
                mb: { sm: '15px', md: 0 },
              }}>
              <Box
                component="a"
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  textDecoration: 'none',
                  color: 'inherit',
                  // a hairline in the divider tone says "opens in Maps" without the heavy dotted rule
                  '& .rfq-address': {
                    textDecorationLine: 'underline !important',
                    textDecorationStyle: 'solid !important',
                    textDecorationThickness: '1px !important',
                    textDecorationColor: `${theme.divider} !important`,
                    textUnderlineOffset: '5px !important',
                  },
                  '&:hover .rfq-address': { textDecorationColor: `${theme.textMuted} !important` },
                  '&:hover .rfq-map-pin': { opacity: 0.8 },
                }}>
                <LocationOnIcon className="rfq-map-pin" sx={{ fontSize: 22, color: theme.accent }} />
                <Box>
                  <Typography
                    className="rfq-address"
                    sx={{ fontSize: { xs: 15, md: 20 }, fontWeight: 400, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
                    {property.address}
                  </Typography>
                  {cityStateZip && (
                    <Typography
                      className="rfq-address"
                      sx={{ fontSize: { xs: 15, md: 20 }, fontWeight: 400, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
                      {cityStateZip}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Mobile-only client info — person icon + name/phone/email, right of icon,
                aligned with address. Real JS conditional (isMobile), not CSS display
                breakpoint objects — those proved unreliable for this component and
                caused this block and its desktop counterpart to render simultaneously. */}
            {isMobile && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '36px 1fr',
                  columnGap: '16px',
                  alignItems: 'start',
                  mt: '20px',
                  mb: '42px',
                }}>
                <PersonIcon sx={{ fontSize: 22, color: theme.accent }} />
                <Box>
                  {issuerSelectable && onIssuerClick ? (
                    <Box
                      component="button"
                      type="button"
                      onClick={onIssuerClick}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                        color: theme.text,
                        fontFamily: 'inherit',
                        fontSize: 14,
                        fontWeight: 600,
                        mb: '8px',
                        lineHeight: 1.2,
                        '&:hover': { opacity: 0.8 },
                      }}>
                      {issuer?.name || 'Homeowner'}
                      <ExpandMoreIcon sx={{ fontSize: 18, color: theme.textMuted }} />
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: 14, fontWeight: 600, mb: '8px', lineHeight: 1.2 }}>
                      {issuer?.name || 'Homeowner'}
                    </Typography>
                  )}
                  {issuer?.phoneNumber && (
                    <Typography sx={{ fontSize: 14, color: theme.text, mb: '4px' }}>
                      {formatPhone(issuer.phoneNumber)}
                    </Typography>
                  )}
                  {issuer?.email && (
                    <Typography sx={{ fontSize: 14, color: theme.text }}>{issuer.email}</Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Project title */}
            <Typography
              sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 400, letterSpacing: '-0.015em', mb: '16px', lineHeight: 1.2 }}>
              Project: {rfq.projectTitle}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 12, sm: 13, md: 14 },
                color: theme.textMuted,
                mb: { sm: '42px', md: '40px' },
                lineHeight: 1.55,
              }}>
              <Box component="span" sx={{ fontWeight: 600, color: theme.text }}>
                Objective:
              </Box>{' '}
              {rfq.objective}
            </Typography>

            {/* Scope */}
            <SectionHeading label="Scope of Work" theme={theme} />
            <Box component="ul" sx={{ pl: '20px', m: 0, mb: { sm: '42px', md: '40px' } }}>
              {rfq.scopeOfWork.map((item, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    fontSize: { xs: 12, sm: 13, md: 14 },
                    color: theme.textMuted,
                    mb: '12px',
                    lineHeight: 1.55,
                  }}>
                  {renderInline(item, theme)}
                </Box>
              ))}
            </Box>
          </Box>

          {/* MIDDLE COLUMN */}
          <Box sx={{ minWidth: 0 }}>
            {/* Header zone wrapper — desktop only; mobile shows client info below address in LEFT column.
                Real JS conditional (!isMobile), not CSS display — see the mobile client info block above. */}
            {!isMobile && (
            <Box
              sx={{
                minHeight: HEADER_ZONE_MIN_HEIGHT,
                mb: 0,
              }}>
              {issuerSelectable && onIssuerClick ? (
                <Box
                  component="button"
                  type="button"
                  onClick={onIssuerClick}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: theme.text,
                    fontFamily: 'inherit',
                    fontSize: { xs: 15, md: 17 },
                    fontWeight: 600,
                    letterSpacing: '-0.015em',
                    mb: { sm: '9px', md: '4px' },
                    lineHeight: 1.2,
                    '&:hover': { opacity: 0.8 },
                  }}>
                  {issuer?.name || 'Homeowner'}
                  <ExpandMoreIcon sx={{ fontSize: 18, color: theme.textMuted }} />
                </Box>
              ) : (
                <Typography
                  sx={{
                    fontSize: { xs: 15, md: 17 },
                    fontWeight: 600,
                    letterSpacing: '-0.015em',
                    mb: { sm: '9px', md: '4px' },
                    lineHeight: 1.2,
                  }}>
                  {issuer?.name || 'Homeowner'}
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: { sm: '10px', md: '24px' },
                  flexWrap: 'wrap',
                }}>
                {issuer?.phoneNumber && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <PhoneIcon sx={{ fontSize: 14, color: theme.textMuted }} />
                    <Typography sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: theme.text }}>
                      {formatPhone(issuer.phoneNumber)}
                    </Typography>
                  </Box>
                )}
                {issuer?.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <EmailIcon sx={{ fontSize: 14, color: theme.textMuted }} />
                    <Typography sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: theme.text }}>
                      {issuer.email}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
            )}

            {/* Timeline — moved to top of middle column */}
            <SectionHeading label="Timeline" theme={theme} />
            {showEditable && edits ? (
              <Box sx={{ mb: { sm: '42px', md: '32px' } }}>
                <EditableDateField
                  label="Desired start date"
                  value={edits.desiredStartDate}
                  onChange={edits.onDesiredStartChange}
                  theme={theme}
                  required
                  leadingIcon={<CalendarTodayIcon sx={{ fontSize: 14, color: theme.accent }} />}
                  isMobile={isMobile}
                />
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: 12, sm: 13, md: 14 },
                  color: theme.textMuted,
                  mb: { sm: '42px', md: '32px' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                <CalendarTodayIcon sx={{ fontSize: 14, color: theme.accent }} />
                <Box component="span" sx={{ fontWeight: 600, color: theme.text }}>
                  Desired start date:
                </Box>{' '}
                {displayStartDate}
              </Typography>
            )}

            {/* Next Steps */}
            <SectionHeading label="Next Steps" theme={theme} />
            <Typography
              sx={{
                fontSize: { xs: 12, sm: 13, md: 14 },
                color: theme.textMuted,
                mb: '24px',
                lineHeight: 1.55,
              }}>
              Contractors are welcome to review prior inspection reports, perform their own onsite
              assessment or order to provide accurate estimates and timelines.
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 500,
                color: theme.text,
                pt: { sm: '10px', md: 0 },
                mb: { sm: '42px', md: '32px' },
                lineHeight: 1.3,
              }}>
              Please call or email to schedule an appointment for a site visit.
            </Typography>

            {/* Submission Requirements */}
            <SectionHeading label="Submission Requirements" theme={theme} />
            {showEditable && edits ? (
              <Box sx={{ mb: '20px' }}>
                <EditableDateField
                  label="Submission deadline"
                  value={edits.deadlineDate}
                  onChange={edits.onDeadlineChange}
                  theme={theme}
                  required
                  leadingIcon={<ArrowForwardIcon sx={{ fontSize: 14, color: theme.accent }} />}
                  isMobile={isMobile}
                />
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: { xs: 12, sm: 13, md: 14 },
                  color: theme.textMuted,
                  mb: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                <ArrowForwardIcon sx={{ fontSize: 14, color: theme.accent }} />
                <Box component="span" sx={{ fontWeight: 600, color: theme.text }}>
                  Submission deadline:
                </Box>{' '}
                {displayDeadline}
              </Typography>
            )}
            {issuer?.email && (
              <Typography sx={{ fontSize: { xs: 12, sm: 13, md: 14 }, color: theme.textMuted, mb: '20px' }}>
                <Box component="span" sx={{ fontWeight: 600, color: theme.text }}>
                  Submit proposal via email to:
                </Box>{' '}
                {issuer.email}
              </Typography>
            )}
            <Box component="ul" sx={{ pl: '20px', m: 0, mt: edits ? '16px' : 0 }}>
              {rfq.submissionRequirements.include.map((item, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    fontSize: { xs: 12, sm: 13, md: 14 },
                    color: theme.textMuted,
                    mb: '8px',
                    lineHeight: 1.55,
                  }}>
                  {item}
                </Box>
              ))}
            </Box>
          </Box>

          {/* RIGHT COLUMN — Important Notice */}
          <Box
            sx={{
              mt: { sm: '42px', md: 0 },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 400, letterSpacing: '-0.015em', mb: '12px' }}>
              Important Notice
            </Typography>
            <Box sx={{ height: '0.5px', backgroundColor: theme.divider, mb: '20px' }} />
            <Typography sx={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.55, mb: '20px' }}>
              Issuing this Request for Quotation (RFQ) does not constitute an obligation on the part
              of the homeowner to award the project or to compensate for any expenses incurred in
              the preparation or submission of a proposal. The homeowner reserves the right to
              reject any or all proposals, modify the scope of work, or cancel the RFQ process at
              any time without prior notice.
            </Typography>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.text, mb: '4px' }}>
              Reservation of Rights
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5, mb: '4px' }}>
              The homeowner reserves the right to:
            </Typography>
            <Box component="ul" sx={{ pl: '18px', m: 0, mb: '18px' }}>
              {[
                'Accept or reject any proposal in whole or in part.',
                'Request additional information or clarification from any bidder.',
                'Negotiate terms and conditions with the selected contractor before finalizing the agreement.',
                'Modify the project scope based on budgetary or other considerations.',
                'Award the project to a contractor that is deemed the most suitable for the homeowner\u2019s needs, which may not necessarily be the lowest bidder.',
              ].map((line, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{ fontSize: 12, color: theme.textMuted, mb: '4px', lineHeight: 1.5 }}>
                  {line}
                </Box>
              ))}
            </Box>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.text, mb: '4px' }}>
              Submission Confidentiality
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5, mb: '18px' }}>
              All submissions will be treated as confidential and used solely for the purpose of
              evaluating contractors for this project. Submissions will not be shared with third
              parties without prior consent.
            </Typography>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.text, mb: '4px' }}>
              Accuracy of Information
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5, mb: '18px' }}>
              The homeowner has made every effort to ensure the accuracy of the information provided
              in this RFQ. However, contractors are responsible for verifying site conditions and
              project requirements prior to submitting their proposals.
            </Typography>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.text, mb: '4px' }}>
              Acceptance of Terms
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5 }}>
              By submitting a proposal, the contractor acknowledges acceptance of all terms and
              conditions outlined in this RFQ, including the disclaimers stated herein.
            </Typography>
            <Box
              sx={{
                // mt:'auto' pushes this to the bottom of the flex column, swallowing
                // any pt added on top of it — use a real margin on mobile instead so
                // the 20px is actually visible rather than absorbed into that gap.
                mt: isMobile ? '20px' : 'auto',
                pt: isMobile ? 0 : '24px',
                pb: isMobile ? '20px' : 0,
                display: 'flex',
                justifyContent: 'flex-end',
                opacity: 0.7,
              }}>
              <HauserLogo style={{ width: 150, height: 'auto' }} />
            </Box>
          </Box>
        </Box>

        {/* Footer brand on each captured page */}
        {mode === 'light' && (
          <Box sx={{ mt: '40px', textAlign: 'right', color: theme.textFaint, fontSize: 12 }}>
            hauserhq.com
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

interface ConsolidatedRfqDocumentProps {
  rfqs: RequestForQuotation[];
  property: RfqPropertyContext;
  issuer?: RfqIssuerContext | null;
  theme: ThemeTokens;
  mode: RenderMode;
  edits?: RfqDocumentEdits;
  readOnly?: boolean;
  onIssuerClick?: (event: React.MouseEvent<HTMLElement>) => void;
  issuerSelectable?: boolean;
}

/**
 * Consolidated layout for compile mode (2+ RFQs) — one document with the
 * shared header (address, issuer) up top, a list of project blocks down the
 * left + middle columns, and shared "Next Steps" + "Important Notice" in
 * the right column.
 */
export const ConsolidatedRfqDocument = ({
  rfqs,
  property,
  issuer,
  theme,
  mode,
  edits,
  readOnly,
  onIssuerClick,
  issuerSelectable,
}: ConsolidatedRfqDocumentProps) => {
  const isMobile = useWindowWidth() <= 768;
  const documentCreated = format(new Date(rfqs[0].createdAt), 'MMMM d, yyyy');
  const cityStateZip = [property.city, [property.state, property.zipCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const mapQuery = [property.address, cityStateZip].filter(Boolean).join(', ');
  const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`;
  const projectCount = rfqs.length;
  const lead = rfqs[0];
  const leadDueDate = new Date(
    lead.createdAt + lead.submissionRequirements.dueInDays * 86400 * 1000,
  );
  const leadDueDateFormatted = format(leadDueDate, 'MMMM d, yyyy');
  const displayStartDate =
    edits?.desiredStartDate != null
      ? format(edits.desiredStartDate, 'MMMM d, yyyy')
      : lead.timeline.desiredStartDate;
  const displayDeadline =
    edits?.deadlineDate != null
      ? format(edits.deadlineDate, 'MMMM d, yyyy')
      : leadDueDateFormatted;
  const showEditable = Boolean(edits) && !readOnly;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          backgroundColor: theme.bg,
          color: theme.text,
          fontFamily: "'Manrope', sans-serif",
          px: { xs: '16px', sm: '20px', md: '48px', lg: '60px' },
          pt: { xs: '16px', sm: '20px', md: '48px', lg: '60px' },
          pb: { xs: '16px', sm: '20px', md: '24px', lg: '30px' },
          maxWidth: '1540px',
          mx: 'auto',
          width: '100%',
          boxSizing: 'border-box',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}>
        {/* Top bar */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', mb: '24px' }}>
          <Typography sx={{ fontSize: 24, fontWeight: 300, letterSpacing: '-0.025em', color: theme.text, lineHeight: 1.2 }}>
            Request for Quote
          </Typography>
          <Typography sx={{ fontSize: 12, color: theme.textFaint, lineHeight: 1.3, mb: isMobile ? '10px' : 0 }}>
            Document Created:{' '}
            <Box component="span" sx={{ color: theme.text, fontWeight: 600 }}>
              {documentCreated}
            </Box>
          </Typography>
        </Box>

        {/* Shared header — same 3-col grid as the body so address sits over the
            Scope column and issuer sits over the Next Steps column. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { sm: '1fr', md: '1fr 1fr 1fr' },
            gap: { sm: '20px', md: '56px' },
            mb: { sm: '42px', md: '48px' },
          }}>
          <Box
            component="a"
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              textDecoration: 'none',
              color: 'inherit',
              mb: { sm: '15px', md: 0 },
              '& .rfq-address': {
                textDecorationLine: 'underline !important',
                textDecorationStyle: 'solid !important',
                textDecorationThickness: '1px !important',
                textDecorationColor: `${theme.divider} !important`,
                textUnderlineOffset: '5px !important',
              },
              '&:hover .rfq-address': { textDecorationColor: `${theme.textMuted} !important` },
              '&:hover .rfq-map-pin': { opacity: 0.8 },
            }}>
            <LocationOnIcon className="rfq-map-pin" sx={{ fontSize: 22, color: theme.accent }} />
            <Box>
              <Typography
                className="rfq-address"
                sx={{ fontSize: { xs: 15, md: 20 }, fontWeight: 400, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
                {property.address}
              </Typography>
              {cityStateZip && (
                <Typography
                  className="rfq-address"
                  sx={{ fontSize: { xs: 15, md: 20 }, fontWeight: 400, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
                  {cityStateZip}
                </Typography>
              )}
            </Box>
          </Box>
          {/* DESKTOP-only client info — inline row. Real JS conditional (!isMobile). */}
          {!isMobile && (
          <Box>
            {issuerSelectable && onIssuerClick ? (
              <Box
                component="button"
                type="button"
                onClick={onIssuerClick}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: theme.text,
                  fontFamily: 'inherit',
                  fontSize: 20,
                  fontWeight: 400,
                  letterSpacing: '-0.02em',
                  mb: '4px',
                  lineHeight: 1.2,
                  '&:hover': { opacity: 0.8 },
                }}>
                {issuer?.name || 'Homeowner'}
                <ExpandMoreIcon sx={{ fontSize: 18, color: theme.textMuted }} />
              </Box>
            ) : (
              <Typography sx={{ fontSize: 20, fontWeight: 400, letterSpacing: '-0.02em', mb: '4px', lineHeight: 1.25 }}>
                {issuer?.name || 'Homeowner'}
              </Typography>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '24px',
                flexWrap: 'wrap',
              }}>
              {issuer?.phoneNumber && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <PhoneIcon sx={{ fontSize: 14, color: theme.textMuted }} />
                  <Typography sx={{ fontSize: 14, color: theme.text }}>
                    {formatPhone(issuer.phoneNumber)}
                  </Typography>
                </Box>
              )}
              {issuer?.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <EmailIcon sx={{ fontSize: 14, color: theme.textMuted }} />
                  <Typography sx={{ fontSize: 14, color: theme.text }}>{issuer.email}</Typography>
                </Box>
              )}
            </Box>
          </Box>
          )}
          {/* Mobile-only client info — person icon + name/phone/email, right of icon,
              aligned with address. Real JS conditional (isMobile). */}
          {isMobile && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '36px 1fr',
              columnGap: '16px',
              alignItems: 'start',
            }}>
            <PersonIcon sx={{ fontSize: 22, color: theme.accent }} />
            <Box>
              {issuerSelectable && onIssuerClick ? (
                <Box
                  component="button"
                  type="button"
                  onClick={onIssuerClick}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    color: theme.text,
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: 600,
                    mb: '8px',
                    lineHeight: 1.2,
                    '&:hover': { opacity: 0.8 },
                  }}>
                  {issuer?.name || 'Homeowner'}
                  <ExpandMoreIcon sx={{ fontSize: 18, color: theme.textMuted }} />
                </Box>
              ) : (
                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: '8px', lineHeight: 1.2 }}>
                  {issuer?.name || 'Homeowner'}
                </Typography>
              )}
              {issuer?.phoneNumber && (
                <Typography sx={{ fontSize: 14, color: theme.text, mb: '4px' }}>
                  {formatPhone(issuer.phoneNumber)}
                </Typography>
              )}
              {issuer?.email && (
                <Typography sx={{ fontSize: 14, color: theme.text }}>{issuer.email}</Typography>
              )}
            </Box>
          </Box>
          )}
        </Box>

        {/* Body — 3-col: Scope (projects) | Next Steps | Important Notice */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { sm: '1fr', md: '1fr 1fr 1fr' },
            gap: { sm: '20px', md: '56px' },
          }}>
          {/* PROJECTS COLUMN */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ mb: '20px' }}>
              <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 400, letterSpacing: '-0.015em', mb: '12px' }}>
                Scope of Work
              </Typography>
              <Box sx={{ height: '0.5px', backgroundColor: theme.divider }} />
            </Box>

            {rfqs.map((rfq, idx) => (
              <Box
                key={rfq.id}
                sx={{
                  mb: idx === rfqs.length - 1 ? 0 : { sm: '24px', md: '32px' },
                  pb: idx === rfqs.length - 1 ? 0 : { sm: '24px', md: '32px' },
                  borderBottom: idx === rfqs.length - 1 ? 'none' : `0.5px solid ${theme.divider}`,
                }}>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: theme.accent,
                    mb: '8px',
                  }}>
                  Project {idx + 1} of {projectCount}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 15, md: 17 },
                    fontWeight: 600,
                    letterSpacing: '-0.015em',
                    mb: '12px',
                    lineHeight: 1.2,
                  }}>
                  {rfq.projectTitle}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 12, sm: 13, md: 14 },
                    color: theme.textMuted,
                    mb: '20px',
                    lineHeight: 1.55,
                  }}>
                  <Box component="span" sx={{ fontWeight: 600, color: theme.text }}>
                    Objective:
                  </Box>{' '}
                  {rfq.objective}
                </Typography>
                <Box component="ul" sx={{ pl: '20px', m: 0 }}>
                  {rfq.scopeOfWork.map((item, i) => (
                    <Box
                      component="li"
                      key={i}
                      sx={{
                        fontSize: { xs: 12, sm: 13, md: 14 },
                        color: theme.textMuted,
                        mb: '8px',
                        lineHeight: 1.55,
                      }}>
                      {renderInline(item, theme)}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          {/* COL 2 — Timeline & Submission (top), Next Steps (below) */}
          <Box sx={{ minWidth: 0 }}>
            {/* Timeline & Submission — single set governing the whole packet */}
            <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 400, letterSpacing: '-0.015em', mb: '12px' }}>
              Timeline &amp; Submission
            </Typography>
            <Box sx={{ height: '0.5px', backgroundColor: theme.divider, mb: '20px' }} />
            {showEditable && edits ? (
              <>
                <EditableDateField
                  label="Desired start date"
                  value={edits.desiredStartDate}
                  onChange={edits.onDesiredStartChange}
                  theme={theme}
                  required
                  leadingIcon={<CalendarTodayIcon sx={{ fontSize: 14, color: theme.accent }} />}
                  isMobile={isMobile}
                />
                <Box sx={{ mb: '20px' }}>
                  <EditableDateField
                    label="Submission deadline"
                    value={edits.deadlineDate}
                    onChange={edits.onDeadlineChange}
                    theme={theme}
                    required
                    leadingIcon={<ArrowForwardIcon sx={{ fontSize: 14, color: theme.accent }} />}
                    isMobile={isMobile}
                  />
                </Box>
              </>
            ) : (
              <>
                <Typography
                  sx={{
                    fontSize: { xs: 12, sm: 13, md: 14 },
                    color: theme.textMuted,
                    mb: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                  <CalendarTodayIcon sx={{ fontSize: 14, color: theme.accent }} />
                  <Box component="span" sx={{ fontWeight: 600, color: theme.text }}>
                    Desired start date:
                  </Box>{' '}
                  {displayStartDate}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: 12, sm: 13, md: 14 },
                    color: theme.textMuted,
                    mb: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}>
                  <ArrowForwardIcon sx={{ fontSize: 14, color: theme.accent }} />
                  <Box component="span" sx={{ fontWeight: 600, color: theme.text }}>
                    Submission deadline:
                  </Box>{' '}
                  {displayDeadline}
                </Typography>
              </>
            )}
            {issuer?.email && (
              <Typography
                sx={{
                  fontSize: { xs: 12, sm: 13, md: 14 },
                  color: theme.textMuted,
                  mb: '20px',
                  mt: showEditable ? '8px' : 0,
                }}>
                <Box component="span" sx={{ fontWeight: 600, color: theme.text }}>
                  Submit proposal via email to:
                </Box>{' '}
                {issuer.email}
              </Typography>
            )}
            <Box
              component="ul"
              sx={{
                pl: '20px',
                m: 0,
                mt: showEditable ? '16px' : 0,
                mb: { sm: '42px', md: '40px' },
              }}>
              {lead.submissionRequirements.include.map((item, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{
                    fontSize: { xs: 12, sm: 13, md: 14 },
                    color: theme.textMuted,
                    mb: '8px',
                    lineHeight: 1.55,
                  }}>
                  {item}
                </Box>
              ))}
            </Box>

            {/* Next Steps — pushed down below Timeline & Submission */}
            <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 400, letterSpacing: '-0.015em', mb: '12px' }}>
              Next Steps
            </Typography>
            <Box sx={{ height: '0.5px', backgroundColor: theme.divider, mb: '20px' }} />
            <Typography
              sx={{
                fontSize: { xs: 12, sm: 13, md: 14 },
                color: theme.textMuted,
                mb: { sm: '10px', md: '24px' },
                lineHeight: 1.55,
              }}>
              Contractors are welcome to review prior inspection reports, perform their own onsite
              assessment or order to provide accurate estimates and timelines.
            </Typography>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 500,
                color: theme.text,
                pt: { sm: '10px', md: 0 },
                lineHeight: 1.35,
              }}>
              Please call or email to schedule an appointment for a site visit.
            </Typography>
          </Box>

          {/* COL 3 — IMPORTANT NOTICE */}
          <Box
            sx={{
              mt: { sm: '42px', md: 0 },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Typography sx={{ fontSize: { xs: 15, md: 17 }, fontWeight: 400, letterSpacing: '-0.015em', mb: '12px' }}>
              Important Notice
            </Typography>
            <Box sx={{ height: '0.5px', backgroundColor: theme.divider, mb: '20px' }} />
            <Typography sx={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.55, mb: '20px' }}>
              Issuing this Request for Quotation (RFQ) does not constitute an obligation on the part
              of the homeowner to award the project or to compensate for any expenses incurred in
              the preparation or submission of a proposal. The homeowner reserves the right to
              reject any or all proposals, modify the scope of work, or cancel the RFQ process at
              any time without prior notice.
            </Typography>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.text, mb: '4px' }}>
              Reservation of Rights
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5, mb: '4px' }}>
              The homeowner reserves the right to:
            </Typography>
            <Box component="ul" sx={{ pl: '18px', m: 0, mb: '18px' }}>
              {[
                'Accept or reject any proposal in whole or in part.',
                'Request additional information or clarification from any bidder.',
                'Negotiate terms and conditions with the selected contractor before finalizing the agreement.',
                'Modify the project scope based on budgetary or other considerations.',
                'Award the project to a contractor that is deemed the most suitable for the homeowner\u2019s needs, which may not necessarily be the lowest bidder.',
              ].map((line, i) => (
                <Box
                  component="li"
                  key={i}
                  sx={{ fontSize: 12, color: theme.textMuted, mb: '4px', lineHeight: 1.5 }}>
                  {line}
                </Box>
              ))}
            </Box>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.text, mb: '4px' }}>
              Submission Confidentiality
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5, mb: '18px' }}>
              All submissions will be treated as confidential and used solely for the purpose of
              evaluating contractors for this project. Submissions will not be shared with third
              parties without prior consent.
            </Typography>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.text, mb: '4px' }}>
              Accuracy of Information
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5, mb: '18px' }}>
              The homeowner has made every effort to ensure the accuracy of the information provided
              in this RFQ. However, contractors are responsible for verifying site conditions and
              project requirements prior to submitting their proposals.
            </Typography>

            <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.text, mb: '4px' }}>
              Acceptance of Terms
            </Typography>
            <Typography sx={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.5 }}>
              By submitting a proposal, the contractor acknowledges acceptance of all terms and
              conditions outlined in this RFQ, including the disclaimers stated herein.
            </Typography>
            <Box
              sx={{
                // mt:'auto' pushes this to the bottom of the flex column, swallowing
                // any pt added on top of it — use a real margin on mobile instead so
                // the 20px is actually visible rather than absorbed into that gap.
                mt: isMobile ? '20px' : 'auto',
                pt: isMobile ? 0 : '24px',
                pb: isMobile ? '20px' : 0,
                display: 'flex',
                justifyContent: 'flex-end',
                opacity: 0.7,
              }}>
              <HauserLogo style={{ width: 150, height: 'auto' }} />
            </Box>
          </Box>
        </Box>

        {mode === 'light' && (
          <Box sx={{ mt: '40px', textAlign: 'right', color: theme.textFaint, fontSize: 12 }}>
            hauserhq.com
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};
